<template>
	<span class="upt-mail" :class="[{ 'upt-mail-selected':segment.selected }]">
		<span class="upt-mail-label" @mouseenter="hoverOnName = true" @mouseleave="hoverOnName = false">
			{{ segment.personal ?? segment.address }}
		</span>
		<anchor-element :tag="SymIcon" :chain="chain" name="menu-drop" class="upt-mail-drop-tick" @click="firstClick=true; chain.toggle();"></anchor-element>
		<boat-element :tag="MenuPanel" :chain="chain" :menu="userMenu"></boat-element>
		<Transition name="fade"><span v-if="showTooltip" class="upt-tooltip">{{ segment.address }}</span></Transition>
	</span>
</template>

<script setup lang="ts">
import SymIcon from "@fd/helpers/SymIcon.vue";
import {MailSegment} from "@fd/editors/mail-select/MailSelectUtils.ts";
import {computed, ref, watch} from "vue";
import {createChain} from "@/ui/paneltree/PanelTree.ts";
import MenuPanel from "@fd/editors/menu/MenuPanel.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import {ExecuteItem, Menu, NavigateItem, OnClickHandler, SeparatorItem} from "@t/ui/Menu.ts";
import {injectEnv} from "@fd/Environment.ts";
import {AlternativeEmails} from "@t/mail/AlternativeEmails.ts";
import {addressToString} from "@t/mail/InternetAddress.ts";

const props = withDefaults(defineProps<{
	readonly?: boolean,
	segment: MailSegment
}>(), { readonly: false });
const env = injectEnv();
const emit = defineEmits(["update", "remove"]);

const firstClick = ref<boolean>(false);
const hoverOnName = ref<boolean>(false);
const showTooltip = computed<boolean>(() => {
	return !!props?.segment?.personal && hoverOnName.value;
});
const altEmails = ref<string[]>([]);

async function loadAlternativeEmails():Promise<void> {
	if (!firstClick.value) return;
	let ep = "api/mails/alt";
	ep = env.net.urlParam(ep, "of", props.segment.address.toLowerCase());
	const nr = await env.net.fetch(ep);
	altEmails.value.length = 0;
	if (nr.ok) {
		const a:AlternativeEmails = await nr.json() as AlternativeEmails;
		altEmails.value.push(a.original);
		altEmails.value.push(...a.alternatives);
		altEmails.value.sort();
	}
}

watch(firstClick, loadAlternativeEmails);

const chain = createChain({
	rule: {
		ah: 0, av: 100,
		bh: 80, bv: 0,
		x: 0, y: 0
	}
}, "mail-tag-menu");

function copyAddress(addressOnly = false):void {
	chain.hide();
	let value: string;
	if (addressOnly) value = props.segment.address;
	else value = addressToString(props.segment);
	navigator.clipboard.writeText(value);
}

function removeAddress():void {
	chain.hide();
	emit("remove");
}

const userMenu = computed<Menu>(() => {
	const m:Menu = {
		type: "menu",
		name: props.segment.address+"_menu",
		children: []
	};
	if (altEmails.value.length === 0) {
		m.children.push(<ExecuteItem>{
			type: "action",
			name: props.segment.address,
			icon: "checkmark-14",
			style: "opacity:0.5;font-weight:500",
			title: props.segment.address,
			onclick: () => {}
		});
	} else {
		for (let email of altEmails.value) {
			let style:string;
			let icon:string;
			let onclick:OnClickHandler;
			if (email.toLowerCase() === props.segment.address.toLowerCase()) {
				style = "opacity:0.5;font-weight:500";
				icon = "checkmark-14";
				onclick = () => {};
			} else {
				style = null;
				icon = "empty-18"
				onclick = () => {
					firstClick.value = false;
					altEmails.value.length = 0;
					emit("update", email);
					chain.hide();
				}
			}
			m.children.push(<ExecuteItem>{
				type: "action",
				name: email,
				icon: icon,
				style: style,
				title: email,
				onclick: onclick
			});

		}
	}
	m.children.push(<SeparatorItem>{ type: "separator" });
	m.children.push(<ExecuteItem>{
		type: "action",
		name: props.segment.address+"_copy_adr",
		icon: "copy",
		title: env.msg("mail.copyAddress"),
		onclick: () => { copyAddress(true) }
	});
	if (props.segment.personal && props.segment.personal !== props.segment.address) {
		m.children.push(<ExecuteItem>{
			type: "action",
			name: props.segment.address+"_copy_full",
			icon: "copy",
			title: env.msg("mail.copyRecipient"),
			onclick: () => { copyAddress(false) }
		});
	}
	m.children.push(<ExecuteItem>{
		type: "action",
		name: props.segment.address+"_remove",
		icon: "cross-14",
		title: env.msg("mail.removeRecipient"),
		onclick: removeAddress
	});
	return m;
});
</script>

<style>
.upt-mail {
	--avatar-size: 18px;
	display: inline flex;
	flex-direction: row;
	column-gap: 0;
	align-items: baseline;
	padding: 0 0 0 8px;
	border-radius: calc(var(--avatar-size) / 2 + 2px);
	user-select: none;
	-webkit-user-select:none;

	background-color: var(--pill-bg);
	color: var(--pill-fg);
	box-shadow: var(--pill-shadow);
	position: relative;

	&:hover {
		background-color: var(--pill-bg-hover);
	}

	&.upt-mail-selected {
		background-color: var(--pill-selected-bg);
		color: var(--pill-selected-fg);
		box-shadow: var(--pill-selected-shadow);

		&:hover {
			background-color: var(--pill-selected-bg-hover);
		}
	}
	&.upt-mail-disabled {
		background-color: var(--pill-disabled-bg);
		color: var(--pill-disabled-fg);
		box-shadow: var(--pill-disabled-shadow);

		&:hover {
			background-color: var(--pill-disabled-bg);
		}
	}
}
.upt-tooltip {
	position: absolute;
	top: calc(100% + 2px);
	left: 4px;
	width: auto;
	height: auto;
	font-size: var(--font-size-s);
	line-height: var(--font-size-l);
	border-radius: 5px;
	padding: 0 5px;
	background-color: lightyellow;
	border: 1px solid darkkhaki;
	color: black;
	z-index: var(--drop-z-index);
}
.upt-mail-label {
}
.upt-mail-drop-tick {
	padding-inline: 6px 8px;
	cursor: default;
}
.with-img .upt-mail-label {
	margin-left: calc(var(--avatar-size) + 4px);
	margin-right: 6px;
}

.classic, .light {
	.upt-mail.upt-mail-mail:not(.upt-mail-selected):not(.upt-mail-disabled):not(:hover) {
		background-color: #E0E8FF;
	}
}

.dark {
	.upt-mail.upt-mail-mail:not(.upt-mail-selected):not(.upt-mail-disabled):not(:hover) {
		background-color: #004488;
	}
}
</style>